import { format } from 'date-fns';
import styled from 'styled-components';
import { ICreatorFashionContentsDB } from 'type/wconcepdb';

import CoreSkeletonBox from 'shared/CoreSkeletonBox';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
import { EllipsisText } from 'styles/styled-components/report';
import style from 'styles/styled-components/style';

type Props = {
	dataSources?: ICreatorFashionContentsDB[];
	platform?: 'ig' | 'yt';
	onClose?: () => void;
};
const SearchFashionContent = ({ dataSources, platform, onClose }: Props) => {
	const preview_loading = false;
	const contents = dataSources ?? [];

	const formatter = (date: string) => format(new Date(date), 'yyyy.MM.dd');

	const handleClose = () => {
		onClose?.();
	};

	return (
		<PreviewBox>
			<PreviewTitle>
				<style.Grid>
					<style.Grid flex="between">
						<style.Text typo="p_bold">패션 관련 콘텐츠</style.Text>
						<IconWhiteButton onClick={handleClose}>
							<CloseIconBox>
								<Icon.Close />
							</CloseIconBox>
						</IconWhiteButton>
					</style.Grid>
					<style.Text typo="span" color="theme.colors.gray.gray80_text">
						{`수집된 콘텐츠 중 키워드 일치 항목의 최신 ${contents.length}개 미리보기 제공`}
					</style.Text>
				</style.Grid>
			</PreviewTitle>
			<PostingList>
				{preview_loading &&
					Array(3)
						.fill(null)
						.map((_, idx) => (
							<PostingItem key={`skeleton-${idx}`}>
								<CoreSkeletonBox _width="80px" _height="80px" />
								<PostingContentText>
									<CoreSkeletonBox _width="100%" _height="20px" />
									<CoreSkeletonBox _width="100%" _height="20px" />
								</PostingContentText>
							</PostingItem>
						))}
				{!preview_loading &&
					contents.map((content, idx) => (
						<PostingItem key={idx}>
							<PostingContentText>
								<PostingDate typo="span">
									{formatter(content.publishedAt ?? '')}
								</PostingDate>
								<style.Grid
									_width="56px"
									_height="16px"
									flex="center"
									align_items="center"
								>
									<CoreSvg _size="12px">
										{platform === 'ig' ? <Icon.FavoriteOff /> : <Icon.EyeView />}
									</CoreSvg>
									<style.Text typo="span" color="theme.colors.gray.gray80_text">
										{content.likeCount} 회
									</style.Text>
								</style.Grid>
							</PostingContentText>
							<style.Grid flex="start" _gap="8px">
								<PostingThumbnail>
									<PostingImage
										src={content.contentImage}
										alt={`Fashion Post ${idx + 1}`}
									/>
								</PostingThumbnail>
								<Grid
									_height="56px"
									align_items="start"
									style={{
										flex: 1,
										overflow: 'hidden',
									}}
								>
									<EllipsisText
										_width="100%"
										typo="span"
										style={{
											display: '-webkit-box',
											WebkitLineClamp: 3,
											WebkitBoxOrient: 'vertical',
											overflow: 'hidden',

											maxHeight: '54px',
											lineHeight: '18px',
											wordBreak: 'break-all',
											whiteSpace: 'pre-wrap',
										}}
									>
										{content.description}
									</EllipsisText>
								</Grid>
							</style.Grid>
						</PostingItem>
					))}
			</PostingList>
		</PreviewBox>
	);
};

const { Grid, Text } = style;

// 스타일드 컴포넌트
const PreviewBox = styled.div`
	width: 320px;
	padding: 16px;
	border: 1px solid ${({ theme }) => theme.colors.gray.gray20};
	border-radius: 8px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	display: flex;
	flex-direction: column;
`;

const PostingList = styled.ul`
	width: 100%;
	margin-top: 8px;
	gap: 16px;
	display: flex;
	flex-direction: column;
`;

const PostingItem = styled.li`
	display: flex;
	justify-content: space-between;
	flex-direction: column;
`;

const PostingThumbnail = styled.div`
	width: 56px;
	height: 56px;
	border-radius: 8px;
	overflow: hidden;
	flex-shrink: 0;
`;

const PostingContentText = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;

	color: ${({ theme }) => theme.colors.gray.gray80_text};
`;

const PostingDate = styled(style.Text)`
	width: 100%;
	color: ${({ theme }) => theme.colors.gray.gray30};
`;

const PostingTitle = styled(style.Text)`
	width: 232px;
	${({ theme }) => theme.common.ellipsis_multi(3)};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
`;

const PreviewTitle = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 8px;
`;

const PostingImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
	border-radius: 8px;
`;

const IconWhiteButton = styled.button`
	width: 30px;
	height: 30px;

	display: flex;
	justify-content: center;
	align-items: center;

	background: white;
	border: none;
	cursor: pointer;
`;

const IconBox = styled.span`
	width: 12px;
	height: 12px;

	display: flex;
	justify-content: center;
	align-items: center;

	color: ${({ theme }) => theme.colors.gray.gray80_text};
`;

const CloseIconBox = styled.span`
	width: 16px;
	height: 16px;

	display: flex;
	justify-content: center;
	align-items: center;

	svg {
		width: 100%;
		height: 100%;
	}
`;

export default SearchFashionContent;
